import { create } from "zustand";
import {
  deleteUser,
  getUsers,
  loginUser,
  adminCreateUser,
  refreshToken as refreshTokenService,
  registerUser,
  updateUser as updateUserService,
} from "../services/auth.service";
import {
  AdminCreateUserRequest,
  JwtPayload,
  LoginRequest,
  LoginResponse,
  RegisterRequest,
  RefreshTokenRequest,
  Role,
  UpdateUserRequest,
  UserSchema,
} from "../types/auth.type";
import {
  checkIsAdmin,
  checkIsCoach,
  checkIsManager,
  hasDashboardAccess,
} from "../utils/roles";
import { jwtDecode } from "jwt-decode";

type GenericResponse<T> = { result: T };

interface AuthState {
  isAuthenticated: boolean;
  isAdmin: boolean;
  isManager: boolean;
  isCoach: boolean;
  userRoles: Role[];
  user: JwtPayload | null;
  token: string | null;
  refreshToken: string | null;
  isLoading: boolean;
  error: string | null;
  login: (credentials: LoginRequest) => Promise<LoginResponse | null>;
  register: (data: RegisterRequest) => Promise<UserSchema | null>;
  logout: () => void;
  initAuth: () => Promise<void>;
  getAllUsers: () => Promise<UserSchema[]>;
  createNewUser: (data: AdminCreateUserRequest) => Promise<UserSchema | null>;
  updateUser: (userId: string, data: UpdateUserRequest) => Promise<UserSchema | null>;
  removeUser: (userId: string) => Promise<boolean>;
}

const unwrap = <T>(response: any): T => response?.result ?? response;

const isExpired = (token?: string | null) => {
  if (!token) return true;
  const decoded = jwtDecode<JwtPayload>(token);
  if (!decoded.exp) return false;
  return Date.now() >= decoded.exp * 1000;
};

const extractRoles = (payload: JwtPayload | null): Role[] => {
  if (!Array.isArray(payload?.roles)) return [];
  return payload.roles.filter((r): r is Role =>
    Object.values(Role).includes(r as Role)
  );
};

const applyAuthSession = (
  access: string,
  refresh: string,
  payload: JwtPayload
) => {
  setSessionTokens(access, refresh);
  const isAdmin = checkIsAdmin(payload);
  const isManager = checkIsManager(payload);
  const isCoach = checkIsCoach(payload);
  return {
    isAuthenticated: true,
    isAdmin,
    isManager,
    isCoach,
    userRoles: extractRoles(payload),
    user: payload,
    token: access,
    refreshToken: refresh,
    isLoading: false,
    error: null,
  };
};

const clearAuthState = (error: string | null = null, isLoading = false) => ({
  isAuthenticated: false,
  isAdmin: false,
  isManager: false,
  isCoach: false,
  userRoles: [] as Role[],
  user: null,
  token: null,
  refreshToken: null,
  isLoading,
  error,
});

const setSessionTokens = (access?: string | null, refresh?: string | null) => {
  if (access) sessionStorage.setItem("access_token", access);
  if (refresh) sessionStorage.setItem("refresh_token", refresh);
};

const clearSessionTokens = () => {
  sessionStorage.removeItem("access_token");
  sessionStorage.removeItem("refresh_token");
};

export const useAuthStore = create<AuthState>((set) => ({
  isAuthenticated: false,
  isAdmin: false,
  isManager: false,
  isCoach: false,
  userRoles: [],
  user: null,
  token: null,
  refreshToken: null,
  isLoading: false,
  error: null,

  login: async (credentials) => {
    set({ isLoading: true, error: null });
    try {
      const response = await loginUser(credentials);
      const data = unwrap<LoginResponse>(response as GenericResponse<LoginResponse>);
      const payload = jwtDecode<JwtPayload>(data.access_token);

      if (!hasDashboardAccess(payload)) {
        clearSessionTokens();
        set({
          ...clearAuthState("Access denied. Admin, manager, or coach role required.", false),
        });
        return null;
      }

      set(applyAuthSession(data.access_token, data.refresh_token, payload));
      return data;
    } catch (error: any) {
      set(clearAuthState(error.response?.data?.detail || "Failed to login", false));
      return null;
    }
  },

  register: async (data) => {
    set({ isLoading: true, error: null });
    try {
      const response = await registerUser(data);
      const result = unwrap<UserSchema>(response as GenericResponse<UserSchema>);
      set({ isLoading: false });
      return result;
    } catch (error: any) {
      set({
        error: error.response?.data?.detail || "Failed to register",
        isLoading: false,
      });
      return null;
    }
  },

  getAllUsers: async () => {
    set({ isLoading: true, error: null });
    try {
      const response = await getUsers();
      const result = unwrap<UserSchema[]>(response as GenericResponse<UserSchema[]>);
      set({ isLoading: false });
      return result || [];
    } catch (error: any) {
      set({
        error: error.response?.data?.detail || "Failed to get users",
        isLoading: false,
      });
      return [];
    }
  },

  createNewUser: async (data) => {
    set({ isLoading: true, error: null });
    try {
      const response = await adminCreateUser(data);
      const result = unwrap<UserSchema>(response as GenericResponse<UserSchema>);
      set({ isLoading: false });
      return result;
    } catch (error: any) {
      set({
        error: error.response?.data?.detail || "Failed to create user",
        isLoading: false,
      });
      return null;
    }
  },

  updateUser: async (userId, data) => {
    set({ isLoading: true, error: null });
    try {
      const response = await updateUserService(userId, data);
      const result = unwrap<UserSchema>(response as GenericResponse<UserSchema>);
      set({ isLoading: false });
      return result;
    } catch (error: any) {
      set({
        error: error.response?.data?.detail || "Failed to update user",
        isLoading: false,
      });
      return null;
    }
  },

  removeUser: async (userId) => {
    set({ isLoading: true, error: null });
    try {
      await deleteUser(userId);
      set({ isLoading: false });
      return true;
    } catch (error: any) {
      set({
        error: error.response?.data?.detail || "Failed to delete user",
        isLoading: false,
      });
      return false;
    }
  },

  logout: () => {
    clearSessionTokens();
    set(clearAuthState(null));
  },

  initAuth: async () => {
    set({ isLoading: true, error: null });
    const accessToken = sessionStorage.getItem("access_token");
    const refreshToken = sessionStorage.getItem("refresh_token");

    if (!accessToken || !refreshToken) {
      set(clearAuthState(null, false));
      return;
    }

    try {
      if (isExpired(accessToken)) {
        const response = await refreshTokenService({
          refresh_token: refreshToken,
        } as RefreshTokenRequest);
        const data = unwrap<LoginResponse>(response as GenericResponse<LoginResponse>);
        const payload = jwtDecode<JwtPayload>(data.access_token);

        if (!hasDashboardAccess(payload)) {
          clearSessionTokens();
          set(clearAuthState(null, false));
          return;
        }

        set(applyAuthSession(data.access_token, data.refresh_token, payload));
        return;
      }

      const payload = jwtDecode<JwtPayload>(accessToken);
      if (!hasDashboardAccess(payload)) {
        clearSessionTokens();
        set(clearAuthState(null, false));
        return;
      }

      set({
        ...applyAuthSession(accessToken, refreshToken, payload),
        refreshToken,
      });
    } catch {
      clearSessionTokens();
      set(clearAuthState(null, false));
    }
  },
}));
